import React from 'react';
import {
  Component,
  ComboWithValue,
  PuzzleInput, FORM_CONTROL
} from "puzzle";
import { GridCellProps, GridColumn as Column } from '@progress/kendo-react-grid';
import { WORKFLOW } from "../../../Types";
import { Tooltip } from '@progress/kendo-react-tooltip';
import { DatePicker } from "@progress/kendo-react-dateinputs";
import classNames from "classnames";
import * as _ from 'lodash';
import { Renderers, getUnixDate } from "puzzle/utility";
import WordCountLink from "../../../../util/WordCountLink/WordCountLink";
import { Menu, MenuItem, MenuSelectEvent } from "@progress/kendo-react-layout";
import { adjustPhaseDueDates, getTableTdAccessibilityProps } from "../../../../util/Util";
import { get, sortBy } from "lodash";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import { completedSubmissionStatuses } from "../../../../util/Util";
import { hasUnfinishedTargets } from "../../../../util/Util";

type BATCH = {
  name: string
  workflowId: number
  targetLanguageInfos: any
  fileCount: number
  totalWordCount: string
  batchId: string
  targetFormat?: string
  dueDate: number
  targets?: any
  uploadedFiles?: any
}

type PROPS = {
  form: FORM_CONTROL,
  initializeUserData: any,
  partiallyAnalyzed?: boolean,
  submissionDueDate: number | null,
  submissionStarted: boolean
}
type STATE = {
  changes: boolean,
  editField: string,
  editedValue: any,
  batchInfos: BATCH[],
  targetLanguageData: any[],
  workflows: WORKFLOW[],
  deletedBatches: any[],
  project: any
}

class BatchesContent extends Component<PROPS, STATE> {
  renders: any;
  tooltip: any;

  public state: STATE = {
    project: null,
    editedValue: null,
    deletedBatches: [],
    batchInfos: [],
    editField: '',
    changes: false,
    targetLanguageData: [],
    workflows: []
  };

  constructor(props: PROPS) {
    super(props);
    this.renders = new Renderers(this.enterEdit, this.exitEdit, 'inEdit');
  }

  /**
   * Enables filed edit depending on submission status, field and policies
   * @param field
   */
  allowEdit = (field: string) => {
    const { submissionMode, submissionStarted, projectPolicies, submissionStatus, hasHumanSteps } = this.props;
    let isEditAllowed = true;
    if (submissionMode === 'EDIT') {
      switch (field) {
        case 'name' :
          isEditAllowed = true;
          break;
        case 'dueDate':
          isEditAllowed = projectPolicies?.hasEditDueDatePolicy && hasHumanSteps;
          if (isEditAllowed && submissionStarted) {
            isEditAllowed = completedSubmissionStatuses.includes(submissionStatus) ? projectPolicies?.hasForcedEditDueDatePolicy : true;
          }
          break;
        default:
          isEditAllowed = !submissionStarted;
          break;
      }
    }
    return isEditAllowed
  };

  batchDateRenderer = (cellProps: GridCellProps) => {

    let { dataItem, field } = cellProps
    const {
      submissionStatus,
      submissionMode,
      projectPolicies,
      hasHumanSteps,
    } = this.props;

    let date;
    if (field) {
      date = this.getHighestDate(dataItem.targetLanguageInfos);
    }
    const inEdit = dataItem.inEdit && this.state.editField === field;
    let tdCls = classNames(
      '',
      {
        'tp-file-name-in-edit': inEdit
      }
    );
    const tdProps = getTableTdAccessibilityProps(cellProps);

    let datePickerDisabled: boolean = false;
    if (submissionMode === "EDIT") {
      datePickerDisabled = completedSubmissionStatuses.includes(submissionStatus);
      if (!datePickerDisabled) {
        datePickerDisabled = !projectPolicies.hasEditDueDatePolicy || !hasUnfinishedTargets(dataItem.targets);
      }
      if (!hasHumanSteps || projectPolicies.hasForcedEditDueDatePolicy) {
        datePickerDisabled = false;
      }
    }

    let submissionStarted = this.props.submissionStarted;
    let ignoreDueDate = !submissionStarted && this.props.form.getData().mslaLevel && !date;

    return (
      <td
        className={tdCls}
        {...tdProps}
      >
        {inEdit ? <>
          <DatePicker
            disabled={!!datePickerDisabled}
            ref={(date: any) => {
              date?._dateInput?.current?.focus();
            }}
            id={dataItem.moduleId}
            value={date || new Date()}
            format={this.translateByNamespace('date.datePickerFormat')}
            onChange={(e: any) => this.itemChange({ dataItem: dataItem, value: getUnixDate(e.value), field: field })}
            min={new Date()}
          />
          <label htmlFor={dataItem.moduleId} className={'sr-only'}>{this.translate('dueDate')}</label>
        </> : (
          <span
            className={ignoreDueDate ? 'k-display-none' : ''}
            onClick={() => {
              this.enterEdit(dataItem, field);
            }}
          >{date ? this.translateByNamespace('date.dateWithTime', {
            date: date
          }) : null}</span>
        )
        }
      </td>
    );
  };

  batchNameRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field || '';
    const inEdit = dataItem.inEdit && this.state.editField === field;
    const tdProps = getTableTdAccessibilityProps(cellProps);

    let tdCls = classNames(
      '',
      {
        'tp-file-name-in-edit': inEdit
      }
    );

    return (<td
      className={tdCls}
      {...tdProps}
    >
      {inEdit ? <PuzzleInput
        ref={(input: any) => input?.inputReference.focus()}
        required
        onChange={(e: any) => {
          if (e.value.search('/') === -1 && e.value.length < 65) {
            this.itemChange({ dataItem: dataItem, value: e.value, field: field })
          }
        }}
        value={dataItem[field]}
      /> : (
        <span
          onClick={() => {
            this.enterEdit(dataItem, field)
          }}
        >{dataItem[field]}</span>
      )
      }
    </td>)
  };

  /**
   * Update batch due dates for specific batch pulled from passed event
   *
   * @param event
   */
  changeSpecificBatchDates = (event: any) => {
    const { batchInfos } = this.state;
    const newBatches = _.cloneDeep(batchInfos);
    newBatches.forEach((batch: any) => {
      let dataItem = event.dataItem;
      if (batch.batchId ? batch.batchId === dataItem.batchId : batch.name === dataItem.name) {
        batch.targetLanguageInfos.forEach((currentTargetLanguageInfos: any) => {
          currentTargetLanguageInfos.dueDate = event.value;
        })
      }
    });
    this.setState({ batchInfos: newBatches }, this.updateContent)

  };

  /**
   * Deletes selected batch
   * @param index
   */
  deleteBatch = (index: number) => {
    if (!this.props.submissionStarted) {
      const { batchInfos, deletedBatches } = this.state;
      let updatedBatchInfos = [...batchInfos];
      let deletedBatch: any = updatedBatchInfos.splice(index, 1)[0];
      if (deletedBatch.batchId) {
        deletedBatches.push(deletedBatch);
      }
      updatedBatchInfos = updatedBatchInfos.map((item: any, index: number) => {
        return {
          ...item,
          moduleId: 'MultiBatchUpload_' + index
        }
      });
      this.setState({ batchInfos: updatedBatchInfos, deletedBatches }, this.updateContent.bind(this, true))
    }
  };

  /**
   * Menu column render
   * @param cellProps
   */
  menuColumnRenderer = (cellProps: GridCellProps) => {
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let dataItem = cellProps.dataItem;
    let dueDate = get(dataItem, 'targetLanguageInfos[0].dueDate');
    return (
      <td
        className={'tp-actions-column'}
        {...tdProps}
      >
        <Menu
          className={'tp-menu-actions'}
          hoverOpenDelay={300}
          hoverCloseDelay={200}
          vertical={true}
          onSelect={this.executeBatchMenuAction.bind(this, cellProps)}
        >
          <MenuItem>
            <MenuItem
              text={this.translate('openBatch')}
              data={{ operation: 'open' }}
              disabled={!dueDate}
            />
            <MenuItem
              text={this.translate('deleteBatch')}
              data={{ operation: 'delete' }}
              disabled={this.props.submissionStarted}
            >
            </MenuItem>
          </MenuItem>
        </Menu>
      </td>
    )
  };

  /**
   * Executes proper action according to operation received through event
   * @param cellProps
   * @param event
   */
  executeBatchMenuAction = (cellProps: GridCellProps, event: MenuSelectEvent) => {
    const data = event.item.data;
    if (data) {
      if (data.operation === 'delete') {
        this.deleteBatch(cellProps.dataIndex);
      } else {
        this.dispatch('openMultiBatchWithFocus', '', {
          focusTarget: cellProps.dataItem.moduleId
        })
      }
    }
  };

  /**
   * Determines which cell should be edited
   *
   * @param dataItem
   * @param field
   */
  enterEdit = (dataItem: any, field: any) => {
    const allowEdit = this.allowEdit(field);
    if (allowEdit) {
      const batchInfos = this.state.batchInfos.map((item: any) => ({
          ...item,
          inEdit: dataItem.batchId ? item.batchId === dataItem.batchId : item.name === dataItem.name ? field : undefined
        })
      );
      this.setState({
        editedValue: field === 'workflow' ? dataItem['workflowId'] : dataItem[field],
        batchInfos,
        editField: field
      });
    }
  };

  exitEdit = () => {
    const { editField, editedValue, batchInfos } = this.state;
    let duplicateBatchName = false;
    if (editField === 'name') {
      duplicateBatchName = (_.uniqBy(batchInfos, "name").length !== batchInfos.length) || (!_.isEmpty(_.find(batchInfos, { name: '' })));
    }
    const updatedBatchInfos = batchInfos.map((item: any) => {
      if (duplicateBatchName && item.inEdit) {
        item.name = editedValue
      }
      if (!item.workflowId && editField === 'workflow') {
        item.workflowId = editedValue;
      }
      if (editField === 'targetLanguageInfos') {
        if (item.targetLanguageInfos?.length) {
          let isLanguagesWithoutDate = item.targetLanguageInfos.findIndex((currentLangItem: any) => currentLangItem.dueDate) === -1;
          if (isLanguagesWithoutDate) {
            let highestDate = this.getHighestDate(editedValue);
            item.targetLanguageInfos = item.targetLanguageInfos.map((languageItem: any) => {
              languageItem.dueDate = highestDate;

              return languageItem;
            });
          }
        } else {
          item.targetLanguageInfos = editedValue;
        }
      }

      return { ...item, inEdit: undefined }
    });
    this.setState({
      editedValue: null,
      batchInfos: updatedBatchInfos,
      editField: undefined,
    });
  };

  /**
   * File count render method
   * @param cellProps
   */
  fileCountRenderer = (cellProps: GridCellProps): JSX.Element => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field || '';
    let fileCount = dataItem[field];
    if (!_.isNumber(dataItem[field])) {
      fileCount = dataItem.droppedFiles?.length || dataItem.targets?.length || 0;
    }
    const tdProps = getTableTdAccessibilityProps(cellProps);

    return <td {...tdProps}>
      {this.translate('files', { count: fileCount })}
    </td>
  };

  wordCountRenderer = (cellProps: GridCellProps) => {
    let { partiallyAnalyzed } = this.props;
    const { dataItem } = cellProps;
    const preExpandJob = {
      batchName: dataItem.name
    };
    const tdProps = getTableTdAccessibilityProps(cellProps);
    if (partiallyAnalyzed && dataItem.wordCount === 0 && dataItem.targets.length) {
      partiallyAnalyzed = dataItem.targets[0].fileFormatName !== "Non Parsable File Format"
    }
    return <td {...tdProps}>
      <WordCountLink
        data={{ ...dataItem, partiallyAnalyzed }}
        groupBy="languages"
        preExpandJob={preExpandJob}
      />
    </td>
  };

  /**
   * Returns highest date of all target languages
   * If there is no init batch date and submission mode is clone, then init value in cell should be null (no cloning dates on clone submission)
   *
   * @param targetLanguageInfos
   */
  getHighestDate = (targetLanguageInfos: any[]) => {
    const { submissionMode } = this.props;
    let date = targetLanguageInfos[0]?.dueDate;
    targetLanguageInfos?.forEach((item: any) => date < item.dueDate ? item.dueDate : date);
    return date ? new Date(date) : _.includes(['CLONE', 'CREATE'], submissionMode) ? null : new Date();
  };

  /**
   * Return a workflow if it exists on a project
   */
  getWorkflowById = (workflowId: number): WORKFLOW | undefined => {
    return _.find(this.state.workflows, { 'id': workflowId });
  };

  /**
   * Updates data on item change
   *
   * @param event
   */
  itemChange = (event: any) => {
    const { submissionDueDate } = this.props;
    let updateState = true;
    switch (event.field) {
      case 'workflow':
        event.dataItem.workflowId = event.value?.id;
        event.dataItem.workflowName = event.value?.name;
        event.dataItem.targetLanguageInfos = event.dataItem.targetLanguageInfos.map((language: any) => {
          return {
            ...language,
            dueDateInfos: []
          }
        });
        break;
      case 'targetLanguageInfos':
        event.dataItem.targetLanguageInfos = event.value.map((language: any) => {
          return {
            ...language,
            dueDate: event.dataItem?.targetLanguageInfos[0]?.dueDate,
            targetLanguage: language.targetLanguage
          }
        });
        break;
      case 'dueDate':
        if (submissionDueDate && event.value > submissionDueDate) {
          updateState = false;
          this.dispatch('changeSubmissionDueDateByBatchDate', '', {
            event: event,
            fromDate: this.translateByNamespace('date.dateWithTime', {
              date: new Date(submissionDueDate)
            }),
            toDate: this.translateByNamespace('date.dateWithTime', {
              date: new Date(event.value)
            })
          })
        } else {
          event.dataItem.targetLanguageInfos = event.dataItem.targetLanguageInfos.map((language: any) => {
            return {
              ...language,
              dueDate: event.value,
              dueDateInfos: language.dueDateInfos ? adjustPhaseDueDates(language.dueDateInfos, event.value) : null
            }
          });
        }
        break;
      default:
        event.dataItem[event.field] = event.value;
        break;
    }
    if (updateState) {
      this.setState({
        changes: true
      }, this.updateContent);
    }
  };

  /**
   * Removes language on x click next to language name
   *
   * @param dataItem
   * @param targetLang
   */
  removeTargetLanguage = (dataItem: any, targetLang: string) => {
    dataItem.targetLanguageInfos = dataItem.targetLanguageInfos.filter((item: any) => item.targetLanguage !== targetLang);
    this.setState({
      changes: true
    }, this.updateContent)
  };

  /**
   *
   * @param workflows
   * @param targetLanguageData
   * @param project
   * @param batchInfos
   */
  setBatchContentData = (workflows: WORKFLOW[], targetLanguageData: any[], project: number, batchInfos: any) => {
    batchInfos = batchInfos.map((item: any, index: number) => this.updateBatchInfos(item, index, targetLanguageData));
    this.setState({ workflows, targetLanguageData, project, batchInfos })
  };

  /**
   * Target language renderer with tag editor in it.
   * Note: Removing target language is possible without entering edit mode clicking on x on right side of tag. If only one target language
   * remains in cell, clicking on x will open edit mode. That is desired behavior.
   *
   * @param cellProps
   */
  targetLanguageRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field;
    let targetLanguageDisplay;
    let targetLanguageName = [];
    if (field) {
      let targetLanguages = dataItem[field];
      targetLanguageDisplay = targetLanguages?.map((targetLanguageData: any, index: number) => {
        let className = classNames(
          'tp-target-lang-tag',
          {
            'tp-target-lang-tag-default': targetLanguageData.default,
            'tp-target-lang-tag-glossary': targetLanguageData.hasGlossary && targetLanguageData.hasTmConfig,
            'tp-target-lang-tag-tm': targetLanguageData.hasTmConfig,
            'tp-target-lang-tag-notInProject': targetLanguageData.notInProject
          }
        );

        return (
          <div
            className={className}
            key={`target-language-item-${targetLanguageData.targetLanguage}-${index}`}
            title={targetLanguageData.notInProject ? 'Language not in project' : undefined}
            onClick={(e: any) => this.openTmConfiguration(e, targetLanguageData)}
          >
            {targetLanguageData.targetLanguageDisplayName}
            {!this.props.submissionStarted && <span className={'k-icon k-i-close'}
                                                    onClick={() => targetLanguages.length > 1 ? this.removeTargetLanguage(dataItem, targetLanguageData.targetLanguage) : this.enterEdit(dataItem, field)}
            />}
          </div>
        )
      });
      targetLanguageName = targetLanguages?.map((targetLanguageData: any) => targetLanguageData.targetLanguageDisplayName);
    }

    let languageNameArray = cellProps.dataItem.targetLanguageInfos;
    let languageListName = languageNameArray?.map((targetLanguageData: any) => targetLanguageData.targetLanguageDisplayName);
    let sortedLanguageListName = sortBy(languageListName);
    let languageTooltiip = sortedLanguageListName.join(',\n');
    const tdProps = getTableTdAccessibilityProps(cellProps);

    return (
      <td
        className={'tp-target-language-column'}
        {...tdProps}
      >
        <Tooltip
          className={'tp-target-lang-tooltip'}
          openDelay={100}
          position={'left'}
        >
          <div
            className={'tp-languages-column'}
            title={languageTooltiip}
          >
            <div
              className="k-hbox k-align-items-center"
              key={`language-${targetLanguageName}}`}
              title={languageTooltiip}
            >
              <i className='tp-icon tp-language tp-space-right-half-1 tp-pointer-events-none' aria-hidden={true} />
              {targetLanguageDisplay.length} {this.translate('language_plural')}
            </div>

          </div>
        </Tooltip>
      </td>
    )
  };

  openTmConfiguration = (e: any, data: any) => {
    const isClosing = e.target.getAttribute('class').includes('k-i-close');
    if (!isClosing) {
      if (data.hasTmConfig) {
        const projectId = this.state.project;
        this.dispatch('openTMConfiguration', '', {
          tmConfigurationData: data,
          projectId,
          hasGlossary: data.hasGlossary
        });
      }
    }
  };

  /**
   * Updates batch dates on due date change
   *
   * @param dueDate
   */
  updateBatchDueDates = (dueDate: number) => {
    let { batchInfos } = this.state;
    let { submissionDueDate } = this.props;
    const newBatches = _.cloneDeep(batchInfos);
    newBatches.forEach((batch: any) => {
      batch.targetLanguageInfos.forEach((currentTargetLanguageInfos: any) => {
        currentTargetLanguageInfos.dueDate = dueDate;
        let currentPhases = currentTargetLanguageInfos.dueDateInfos;
        if (submissionDueDate > dueDate) {
          currentTargetLanguageInfos.dueDateInfos = adjustPhaseDueDates(currentPhases, dueDate);
        } else if (currentPhases && currentPhases.length) {
          currentTargetLanguageInfos.dueDateInfos[currentPhases.length - 1].dueDate = dueDate;
        }
      })
    });
    this.setState({ batchInfos: newBatches }, this.updateContent)
  };

  /**
   * Updates batch info with target language additional data
   *
   * @param item
   * @param index
   * @param targetLanguageData
   */
  updateBatchInfos = (item: any, index: number, targetLanguageData: any) => {
    let targetLanguageInfos = _.cloneDeep(item.targetLanguageInfos).map((currentTargetLanguageData: any) => {
      const matchedTargetLanguage = _.find(targetLanguageData, { targetLanguage: currentTargetLanguageData.targetLanguage });
      return { ...currentTargetLanguageData, ...matchedTargetLanguage };
    });
    item.targetLanguageInfos = targetLanguageInfos;
    return {
      ...item,
      moduleId: 'MultiBatchUpload_' + index
    }
  };

  /**
   * Updates content state, if changeIds flag exists reorganises moduleIds before update.
   * @param changeIds
   */
  updateContent = (changeIds?: boolean) => {
    let { batchInfos, deletedBatches } = this.state;
    if (changeIds) {
      batchInfos = batchInfos.map((item: any, index: number) => {
        return {
          ...item,
          moduleId: 'MultiBatchUpload_' + index
        }
      });
    }
    const batchData = {
      batchInfos,
      deletedBatches
    };
    this.props.changeData(batchData);
  };

  /**
   * Sets uploaded file on the template with more than one batch
   * @param droppedFiles
   */
  updateBatchInfosWithFileFromTemplate = (droppedFiles: any[]) => {
    const { batchInfos } = this.state;
    const uploadedFiles: any [] = (droppedFiles || []).filter((file: any) => file.uploaded !== 'failed');
    batchInfos[0].uploadedFiles = [...uploadedFiles]; //add proper targets - file with all data
    batchInfos[0].fileCount = uploadedFiles.length;
  };

  workflowRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field || '';
    let inEdit = dataItem.inEdit && this.state.editField === 'workflow';
    let tdCls = classNames(
      'tp-workflow-column',
      {
        'tp-file-name-in-edit': inEdit
      }
    );
    let workflow = this.getWorkflowById(dataItem.workflowId);
    const tdProps = getTableTdAccessibilityProps(cellProps);

    let spanClassName = `pd-workflow-name${workflow ? '' : ' k-invalid'}`

    return (<td
      className={tdCls}
      {...tdProps}
    >
      {inEdit ?
        <ComboWithValue
          ref={(combo: any) => {
            combo?.component?._input?.focus();
          }}
          className={'tp-target-lang-tagfield'}
          name="workflow"
          onChange={(e: any) => this.itemChange({ dataItem: dataItem, value: e.value, field: field })}
          data={this.state.workflows}
          textField='name'
          value={dataItem.workflowId}
          valueField='id'
        /> :
        <span className={spanClassName} style={{ display: 'block' }} onClick={() => {
          this.enterEdit(dataItem, field);
        }}
        >{workflow?.name}</span>
      }
    </td>)
  };

  render() {
    let { batchInfos } = this.state;
    const isTemplate = ['TEMPLATE_EDIT', 'TEMPLATE_CREATE'].includes(this.props.submissionMode);
    const gridStyle = classNames(
      'tp-uploaded-files-grid tp-content-batches',
      {
        'tp-content-batches-template-page': isTemplate
      }
    );
    return (
      <>
        {batchInfos &&
          <div className={'tp-page-container-content-inner-wrap'}>
            {!!batchInfos.length &&
              <KendoGridAbstract
                captionText={this.translate('SRL-batchesTableCaption')}
                onItemChange={this.itemChange}
                className={gridStyle}
                editField='inEdit'
                data={batchInfos}
                resizable
                rowRender={this.renders.rowRender}
              >
                <Column
                  field="name"
                  title={this.translate('batchName')}
                  cell={this.batchNameRenderer}
                  minResizableWidth={100}
                  width={isTemplate ? 255 : 150}
                />
                <Column
                  field="workflow"
                  title={this.translate('workflow')}
                  cell={this.workflowRenderer}
                  minResizableWidth={100}
                  width={isTemplate ? 256 : 147}
                />
                {!isTemplate && [
                  <Column
                    key='conditionalColumn1'
                    field="dueDate"
                    title={this.translate('dueDate')}
                    cell={this.batchDateRenderer}
                    minResizableWidth={100}
                    width={150}
                  />,
                  <Column
                    key='conditionalColumn3'
                    field="wordCount"
                    title={this.translate('wordCount')}
                    className={'tp-word-count-column'}
                    editable={false}
                    cell={this.wordCountRenderer}
                    minResizableWidth={100}
                    width={100}
                  />,
                  <Column
                    key='conditionalColumn2'
                    field="fileCount"
                    title={this.translate('fileCount')}
                    editable={false}
                    cell={this.fileCountRenderer}
                    minResizableWidth={100}
                    width={100}
                  />
                ]}
                <Column
                  field="targetLanguageInfos"
                  title={this.translate('targetLanguages')}
                  cell={this.targetLanguageRenderer}
                  minResizableWidth={100}
                  width={isTemplate ? 255 : 120}
                />
                <Column
                  field="menu"
                  title={this.translate("menuCell")}
                  className={'tp-sr-only-cell'}
                  editable={false}
                  cell={this.menuColumnRenderer}
                  minResizableWidth={100}
                  width={100}
                />
              </KendoGridAbstract>}
          </div>
        }
      </>
    );
  }
}

export default BatchesContent;
